import {
  AlertBanner,
  Box,
  Button,
  ButtonIcon,
  Chip,
  Icon,
  IconKind,
  Typography,
} from "@saviynt/design-system";
import { FormattedMessage, useIntl } from "react-intl";
import { useRouter } from "@tanstack/react-router";
import "./PoliciesFormHeader.css";
import {
  useCreatePolicy,
  useUpdateClonePolicy,
} from "../../../hooks/usePolicy";
import { usePoliciesForm } from "../PoliciesFormContext";
import { Policy } from "@repo/api/schemas/policies.schema";
import { ActionType } from "../PoliciesForm";
import "./PoliciesFormHeader.css";
import PolicyActionModal from "../../PoliciesPageList/PolicyActionModal/PolicyActionModal";
import { useState, useRef, useMemo } from "react";

interface PoliciesFormHeaderProps {
  actionType: ActionType;
  policyData?: Policy;
  headerClassName?: string;
}

export default function PoliciesFormHeader({
  actionType,
  policyData = undefined,
  headerClassName,
}: PoliciesFormHeaderProps) {
  const intl = useIntl();
  const router = useRouter();
  const category = new URLSearchParams(window.location.search).get("category");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [banner, setBanner] = useState<{
    colorTheme: "Critical";
    title: string;
  } | null>(null);
  const { name, description, severity, tags, evaluationCriteria } =
    usePoliciesForm();

  const initialState = useRef({
    name,
    description,
    severity,
    tags,
    evaluationCriteria,
  });

  const isFormChanged = useMemo(
    () =>
      name !== initialState.current.name ||
      description !== initialState.current.description ||
      severity !== initialState.current.severity ||
      JSON.stringify(tags) !== JSON.stringify(initialState.current.tags) ||
      evaluationCriteria !== initialState.current.evaluationCriteria,
    [name, description, severity, tags, evaluationCriteria],
  );

  const createPolicyMutate = useCreatePolicy({
    onError: (message) => setBanner({ colorTheme: "Critical", title: message }),
    onSuccess: () => {
      router.history.back();
    },
  });

  const updateClonePolicyMutate = useUpdateClonePolicy({
    onError: (message) => setBanner({ colorTheme: "Critical", title: message }),
    onSuccess: () => {
      router.history.back();
    },
  });

  const config = {
    new: {
      title: {
        defaultMessage: "New Policy",
        id: "newPolicyTitle",
        description: "Title for creating new policy modal",
      },
      icon: "Policy",
    },
    edit: {
      title: {
        defaultMessage: "Edit Policy",
        id: "editPolicyTitle",
        description: "Title for editing policy modal",
      },
      icon: "Edit",
    },
    clone: {
      title: {
        defaultMessage: "Clone Policy",
        id: "clonePolicyTitle",
        description: "Title for cloning policy modal",
      },
      icon: "Copy",
    },
  };

  const headerIcon = config[actionType].icon;
  let title: React.ReactNode = intl.formatMessage(config[actionType].title);
  let subtitle: React.ReactNode = "";

  if (actionType === "edit" && policyData) {
    const { name: policyName, version, parent_policy_name } = policyData;
    title = (
      <Box className="flex gap-2">
        <Typography kind="h2" desktop="h2-d" mobile="h2-d">
          {intl.formatMessage(config[actionType].title)}
        </Typography>
        <Chip
          kind="subtleFilled"
          label={`v${version}`}
          color="neutral"
          size="small"
        />
        {!!parent_policy_name && (
          <Chip
            kind="subtleFilled"
            label={parent_policy_name}
            color="info"
            size="small"
            leftIcon={<Icon kind="Branch" size="xSmall" />}
          />
        )}
      </Box>
    );
    subtitle = policyName;
  }

  if (actionType === "clone" && policyData) {
    const { name: policyName } = policyData;
    subtitle = (
      <Box className="flex gap-1 items-center">
        <Icon kind="Branch" size="xSmall" />
        <FormattedMessage
          defaultMessage="Cloning from"
          id="ffxMHN"
          description="Subtitle for cloning policy modal"
        />
        <Typography
          kind="body2"
          desktop="body2-d"
          mobile="body2-d"
          color="--color-foreground-info-medium"
        >
          {policyName}
        </Typography>
      </Box>
    );
  }

  const headerClasses = [
    "PoliciesForm-header",
    subtitle ? "PoliciesForm-header--hasSubtitle" : "",
    headerIcon ? "PoliciesForm-header--hasIcon" : "",
    headerClassName ?? "",
  ]
    .filter(Boolean)
    .join(" ");

  const handleSave = () => {
    const payload = {
      name,
      description,
      rule_definition: { evaluationCriteria },
      severity,
      metadata: { tags },
      is_active: true,
      category: actionType === "new" ? category : policyData?.category,
    };

    if (actionType === "new") {
      createPolicyMutate.mutate({ payload });
    } else if (actionType === "clone" || actionType === "edit") {
      const { id: policyId } = policyData!;
      updateClonePolicyMutate.mutate({ policyId, payload, actionType });
    }
  };

  return (
    <>
      <PolicyActionModal
        variant="discard"
        isOpen={isModalOpen}
        setIsOpen={() => setIsModalOpen(false)}
        onConfirm={() => {
          router.history.back();
        }}
      />
      <header className="PoliciesForm-headerWrapper">
        <div className={headerClasses}>
          <ButtonIcon
            kind="ghost"
            icon={<Icon kind="ChevronLeft" size="medium" />}
            ariaLabel={intl.formatMessage({
              defaultMessage: "Back button",
              id: "6IE8cX",
            })}
            onClick={() => router.history.back()}
          />

          <ButtonIcon
            size="large"
            kind="outlinedSecondary"
            icon={
              <Icon
                kind={headerIcon as IconKind}
                size="medium"
                color="--color-foreground-primary-medium,"
              />
            }
            ariaLabel=""
          />

          <div className="PoliciesForm-titleContainer">
            <Typography kind="h2" desktop="h2-d" mobile="h2-d">
              {title}
            </Typography>
            {subtitle && (
              <Typography
                kind="body2"
                desktop="body2-d"
                mobile="body2-d"
                color="--color-foreground-neutral-subtle"
              >
                {subtitle}
              </Typography>
            )}
          </div>
        </div>
        <div className="PoliciesForm-actionBtn flex gap-8 ml-auto">
          <Button
            kind="outlined"
            size="medium"
            leftIcon={<Icon kind="Undo" label="Undo" />}
            onClick={() => setIsModalOpen(true)}
            isDisabled={!isFormChanged}
          >
            <FormattedMessage
              defaultMessage="Discard"
              id="LtmPVk"
              description="Text for discard button"
            />
          </Button>
          <Box>
            <Button
              kind="filled"
              size="medium"
              onClick={handleSave}
              leftIcon={<Icon kind="Saved" label="Undo" />}
            >
              <FormattedMessage
                defaultMessage="Save Policy"
                id="yrXfhT"
                description="Text for confirm button"
              />
            </Button>
          </Box>
        </div>
      </header>
      {banner && (
        <AlertBanner
          colorTheme={banner.colorTheme}
          title={banner.title}
          isFloating={false}
          onCancel={() => setBanner(null)}
          isColonVisible={false}
        />
      )}
    </>
  );
}
